import type {
  LoginEventEntry,
  LoginEventsPage,
  PlatformLoginEventType,
} from './loginHistory.types'

export const LOGIN_EVENT_TYPE_LABELS: Record<PlatformLoginEventType, string> = {
  login_success: 'Succesvol',
  login_blocked: 'Geblokkeerd',
}

function asInt (value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value)
  }
  return fallback
}

function asMetadata (value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }
  return value as Record<string, unknown>
}

export function parsePlatformLoginEventType (value: unknown): PlatformLoginEventType {
  if (value === 'login_blocked') return 'login_blocked'
  return 'login_success'
}

function asLoginEventEntry (value: unknown): LoginEventEntry | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const row = value as Record<string, unknown>
  const id = typeof row.id === 'string' ? row.id : ''
  const userId = typeof row.user_id === 'string' ? row.user_id : ''
  if (!id || !userId) return null

  return {
    id,
    userId,
    email: typeof row.email === 'string' ? row.email : '',
    displayName: typeof row.display_name === 'string' ? row.display_name : '',
    eventType: parsePlatformLoginEventType(row.event_type),
    userAgent: typeof row.user_agent === 'string' ? row.user_agent : null,
    metadata: asMetadata(row.metadata),
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
  }
}

export function parseLoginEventsPage (raw: unknown): LoginEventsPage {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Ongeldige login history response')
  }

  const data = raw as Record<string, unknown>
  const items = Array.isArray(data.items)
    ? data.items.map(asLoginEventEntry).filter((item): item is LoginEventEntry => item !== null)
    : []

  return {
    items,
    total: asInt(data.total),
    limit: asInt(data.limit, 50),
    offset: asInt(data.offset),
  }
}

export function getLoginEventTypeLabel (eventType: PlatformLoginEventType): string {
  return LOGIN_EVENT_TYPE_LABELS[eventType]
}

export function formatLoginEventUser (entry: LoginEventEntry): string {
  const name = entry.displayName.trim()
  if (name) return name
  if (entry.email.trim()) return entry.email.trim()
  return entry.userId
}

export function formatUserAgentSummary (userAgent: string | null): string {
  if (!userAgent?.trim()) return '—'
  const trimmed = userAgent.trim()
  if (trimmed.length <= 72) return trimmed
  return `${trimmed.slice(0, 69)}…`
}

const LOGIN_DEDUP_KEY_PREFIX = 'sa_login_logged:'
const LOGIN_BLOCKED_DEDUP_MS = 30_000
/** Zelfde auth-sessie (access token) mag maar één login_success loggen. */
const LOGIN_SUCCESS_DEDUP_MS = 12 * 60 * 60 * 1000

function readLoginDedupStorage (): Storage | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage
}

export function loginEventDedupKey (eventType: PlatformLoginEventType, dedupId: string): string {
  return `${LOGIN_DEDUP_KEY_PREFIX}${eventType}:${dedupId}`
}

export function hasLoginEventBeenLogged (
  eventType: PlatformLoginEventType,
  dedupId: string,
  ttlMs: number
): boolean {
  const storage = readLoginDedupStorage()
  if (!storage) return false

  const key = loginEventDedupKey(eventType, dedupId)
  try {
    const raw = storage.getItem(key)
    if (!raw) return false

    const loggedAt = Number.parseInt(raw, 10)
    if (!Number.isFinite(loggedAt) || Date.now() - loggedAt > ttlMs) {
      storage.removeItem(key)
      return false
    }

    return true
  } catch {
    return false
  }
}

export function markLoginEventLogged (
  eventType: PlatformLoginEventType,
  dedupId: string
): void {
  const storage = readLoginDedupStorage()
  if (!storage) return

  try {
    storage.setItem(loginEventDedupKey(eventType, dedupId), String(Date.now()))
  } catch {
    /* quota / private mode */
  }
}

export function clearLoginEventLogged (
  eventType: PlatformLoginEventType,
  dedupId: string
): void {
  const storage = readLoginDedupStorage()
  if (!storage) return

  try {
    storage.removeItem(loginEventDedupKey(eventType, dedupId))
  } catch {
    /* ignore */
  }
}

export function tryClaimLoginEvent (
  eventType: PlatformLoginEventType,
  dedupId: string,
  ttlMs: number
): boolean {
  if (hasLoginEventBeenLogged(eventType, dedupId, ttlMs)) return false
  markLoginEventLogged(eventType, dedupId)
  return true
}

export function buildLoginSuccessDedupId (userId: string, accessToken: string): string {
  return `${userId}:${accessToken.slice(-32)}`
}

export function buildLoginBlockedDedupId (userId: string): string {
  return userId
}

export function getLoginEventDedupTtlMs (eventType: PlatformLoginEventType): number {
  return eventType === 'login_blocked' ? LOGIN_BLOCKED_DEDUP_MS : LOGIN_SUCCESS_DEDUP_MS
}

